import React, { useState, useEffect } from "react";
import "./Addusers.css";
import { X } from "lucide-react";
import axios from "axios";

interface Props {
  open: boolean;
  type: "buyer" | "seller";
  onClose: () => void;
  token: string;
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
}

const RegisterUserModal: React.FC<Props> = ({ open, type, onClose, token }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | "">("");
  const [loading, setLoading] = useState(false);
  const [businessTypes, setBusinessTypes] = useState<any[]>([]);
  const [productTypes, setProductTypes] = useState<any[]>([]);

  // Buyer state
  const [buyerData, setBuyerData] = useState({
    firstName: "",
    Mobilenumber: "",
    email: "",
  });

  // Seller state
  const [sellerData, setSellerData] = useState({
    userId: "",
    firstName: "",
    lastName: "",
    businessName: "",
    email: "",
    city: "",
    state: "",
    panNumber: "",
    panImage: "",
    aadhaarNumber: "",
    gstNumber: "",
    gstCertificateImage: "",
    TypeofBusiness: "",
    mostlydeal: "",
    bankName: "",
    AccountHolderName: "",
    AccountNumber: "",
    IFSCCode: "",
    cancelCheckImage: ""
  });

  // Fetch users for dropdown
  useEffect(() => {
    if (open) {
      fetchUsers();
      fetchBusinessTypes();
      fetchProductTypes();
    }
  }, [open]);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        "https://api.sonachandiwale.com/api/v1/buyers",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // console.log("buyers api:", response.data);

      // handle both structures
      const usersData = Array.isArray(response.data)
        ? response.data
        : response.data.data;

      setUsers(usersData || []);

    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessTypes = async () => {
    try {
      const response = await axios.get(
        "https://api.sonachandiwale.com/api/v1/type-of-business/getAll"
      );

      // console.log("Business Types:", response.data);

      setBusinessTypes(response.data.data || response.data);

    } catch (error) {
      console.error("Error fetching business types:", error);
    }
  };

  const fetchProductTypes = async () => {
    try {
      const response = await axios.get(
        "https://api.sonachandiwale.com/api/v1/product-information/getAll"
      );

      // console.log("Product Types:", response.data);

      const data = Array.isArray(response.data)
        ? response.data
        : response.data.data;

      setProductTypes(data || []);

    } catch (error) {
      console.error("Error fetching product types:", error);
    }
  };

  const handleUserSelect = (userId: number) => {
    setSelectedUserId(userId);
    const selectedUser = users.find(user => user.id === userId);

    if (selectedUser && type === "seller") {
      // Auto-fill seller data from selected user
      setSellerData(prev => ({
        ...prev,
        userId: userId.toString(),
        firstName: selectedUser.firstName || "",
        lastName: selectedUser.lastName || "",
        email: selectedUser.email || ""
      }));
    }
  };

  const handleBuyerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setBuyerData((prev) => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSellerChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setSellerData(prev => ({
      ...prev,
      [name]: value
    }));
  };

const handleFileUpload = async (file: File, fieldName: string) => {
  try {
    const formData = new FormData();
    formData.append("image", file); // 👈 backend field name

    const response = await axios.post(
      "https://api.sonachandiwale.com/api/v1/uplordDoc",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      }
    );

    const filePath = response.data.data;

    setSellerData((prev) => ({
      ...prev,
      [fieldName]: filePath
    }));

  } catch (error) {
    console.error("Upload error:", error);
  }
};

  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (type === "buyer") {
        // Buyer API call
        const response = await axios.post(
          "https://api.sonachandiwale.com/api/v1/admin/create-account",
          buyerData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );
        // console.log("Buyer registered:", response.data);
      } else {
        // Seller API call
        const response = await axios.post(
          "https://api.sonachandiwale.com/api/v1/seller/register",
          sellerData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );
        // console.log("Seller registered:", response.data);
      }

      onClose();
      // Reset forms
      setSelectedUserId("");
      setBuyerData({
        firstName: "",
        Mobilenumber: "",
        email: ""
      });
      // Reset seller data
    } catch (error) {
      console.error("Error registering:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="register-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content-scroll">
          <div className="modal-header">
            <div>
              <h2>
                {type === "buyer" ? "Register New User" : "Register New Seller"}
              </h2>
              <p>
                {type === "buyer"
                  ? "Enter user details to register a new buyer"
                  : "Enter seller details for verification"}
              </p>
            </div>
            <X className="close-icon" onClick={onClose} />
          </div>
          {type === "buyer" ? (
            <div className="grid-2">

              <div>
                <label>First Name *</label>
                <input
                  name="firstName"
                  placeholder="Enter first name"
                  value={buyerData.firstName}
                  onChange={handleBuyerChange}
                />
              </div>

              <div>
                <label>Mobile Number *</label>
                <input
                  name="Mobilenumber"
                  placeholder="+91 XXXXX XXXXX"
                  value={buyerData.Mobilenumber}
                  onChange={handleBuyerChange}
                />
              </div>

              <div>
                <label>Email Address *</label>
                <input
                  name="email"
                  placeholder="email@example.com"
                  value={buyerData.email}
                  onChange={handleBuyerChange}
                />
              </div>

            </div>
          ) : (
            /* -------- SELLER FORM -------- */
            <>
              {/* -------- BASIC DETAILS -------- */}
              <div className="form-section">
                <h4 className="section-title">Basic Details</h4>
                <div className="grid-2">
                  <div className="user-select-section">
                    <label>Select User *</label>
                    <select
                      value={selectedUserId}
                      onChange={(e) => handleUserSelect(Number(e.target.value))}
                      className="user-dropdown"
                    >
                      <option value="">-- Select a user --</option>

                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.firstName || "No Name"} - {user.mobileNumber || user.email || "No Contact"}
                        </option>
                      ))}

                    </select>
                  </div>
                  <div>
                    <label>First Name *</label>
                    <input
                      name="firstName"
                      placeholder="Enter first name"
                      value={sellerData.firstName}
                      onChange={handleSellerChange}
                    />
                  </div>
                  <div>
                    <label>Last Name *</label>
                    <input
                      name="lastName"
                      placeholder="Enter last name"
                      value={sellerData.lastName}
                      onChange={handleSellerChange}
                    />
                  </div>
                  <div>
                    <label>Business Name *</label>
                    <input
                      name="businessName"
                      placeholder="Enter business name"
                      value={sellerData.businessName}
                      onChange={handleSellerChange}
                    />
                  </div>
                  <div>
                    <label>Email Address *</label>
                    <input
                      name="email"
                      placeholder="email@example.com"
                      value={sellerData.email}
                      onChange={handleSellerChange}
                    />
                  </div>
                  <div>
                    <label>City *</label>
                    <input
                      name="city"
                      placeholder="Enter city"
                      value={sellerData.city}
                      onChange={handleSellerChange}
                    />
                  </div>
                  <div>
                    <label>State *</label>
                    <input
                      name="state"
                      placeholder="Enter state"
                      value={sellerData.state}
                      onChange={handleSellerChange}
                    />
                  </div>
                </div>
              </div>

              {/* -------- DOCUMENT NUMBERS -------- */}
              <div className="form-section">
                <h4 className="section-title">Document Numbers</h4>
                <div className="grid-3">
                  <div>
                    <label>PAN Number *</label>
                    <input
                      name="panNumber"
                      placeholder="Enter PAN number"
                      value={sellerData.panNumber}
                      onChange={handleSellerChange} 
                    />
                  </div>
                  <div>
                    <label>Aadhaar Number *</label>
                    <input
                      name="aadhaarNumber"
                      placeholder="Enter Aadhaar number"
                      value={sellerData.aadhaarNumber}
                      onChange={handleSellerChange}
                    />
                  </div>
                  <div>
                    <label>GST Number</label>
                    <input
                      name="gstNumber"
                      placeholder="Enter GST number"
                      value={sellerData.gstNumber}
                      onChange={handleSellerChange}
                    />
                  </div>
                </div>
              </div>

              {/* -------- DOCUMENT UPLOADS -------- */}
              <div className="form-section">
                <h4 className="section-title">Upload Documents</h4>
                <div className="grid-3">
                  <div className="upload-box">
                    <input
                      type="file"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          handleFileUpload(e.target.files[0], "panImage");
                        }
                      }}
                    />
                    <label htmlFor="panImage">📄 Upload PAN</label>
                  </div>
                  <div className="upload-box">
                    <input
                      type="file"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          handleFileUpload(e.target.files[0], "gstCertificateImage");
                        }
                      }}
                    />
                    <label htmlFor="gstImage">📄 Upload GST</label>
                  </div>
                  <div className="upload-box">
                    <input
                      type="file"
                      onChange={(e) => {
                        if (e.target.files?.[0]) {
                          handleFileUpload(e.target.files[0], "cancelCheckImage");
                        }
                      }}
                    />
                    <label htmlFor="chequeImage">📄 Upload Cheque</label>
                  </div>
                </div>
              </div>

              {/* -------- BUSINESS DETAILS -------- */}
              <div className="form-section">
                <h4 className="section-title">Business Details</h4>
                <div className="grid-2">
                  <div>
                    <label>Type of Business *</label>
                    <select
                      name="TypeofBusiness"
                      value={sellerData.TypeofBusiness}
                      onChange={handleSellerChange}
                    >
                      <option value="">Select type</option>

                      {businessTypes.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label>Mostly Deal In *</label>
                    <select
                      name="mostlydeal"
                      value={sellerData.mostlydeal}
                      onChange={handleSellerChange}
                    >
                      <option value="">Select category</option>

                      {productTypes.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* -------- BANK DETAILS -------- */}
              <div className="form-section">
                <h4 className="section-title">Bank Details</h4>
                <div className="grid-2">
                  <div>
                    <label>Bank Name *</label>
                    <input
                      name="bankName"
                      placeholder="Enter bank name"
                      value={sellerData.bankName}
                      onChange={handleSellerChange}
                    />
                  </div>
                  <div>
                    <label>Account Number *</label>
                    <input
                      name="AccountNumber"
                      placeholder="Enter account number"
                      value={sellerData.AccountNumber}
                      onChange={handleSellerChange}
                    />
                  </div>
                  <div>
                    <label>Account Holder Name *</label>
                    <input
                      name="AccountHolderName"
                      placeholder="Enter account holder name"
                      value={sellerData.AccountHolderName}
                      onChange={handleSellerChange}
                    />
                  </div>
                  <div>
                    <label>IFSC Code *</label>
                    <input
                      name="IFSCCode"
                      placeholder="XXXX0001234"
                      value={sellerData.IFSCCode}
                      onChange={handleSellerChange}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* -------- ACTIONS (Fixed at Bottom) -------- */}
        <div className="modal-actions">
          <button className="cancel-btn" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="submit-btn"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Processing..." : (type === "buyer" ? "Register User" : "Submit for Verification")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegisterUserModal;